import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";
import { environment } from "environments/environment";
import { Product, ReturnRequestDetail } from "../model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "../shared/dialog-base.component";
import { DomainService } from "../shared/domain.service";

export interface RecoverRrdDialogParams extends DialogParams {
  rrdId: number;
}

interface RecoveryImpact {
  ItemDetailIds?: string[];
  ItemDetailAddonIds?: number[];
  CreditMemoIds?: number[];
  Blockers?: string[];
  CanRecover?: boolean;
  CurrentPhase?: string;
  itemDetailIds?: string[];
  itemDetailAddonIds?: number[];
  creditMemoIds?: number[];
  blockers?: string[];
  canRecover?: boolean;
  currentPhase?: string;
}

/**
 * Pass 6 dialog for Recovery — any active phase → RECEIVING reset.
 *
 * On open, calls ComputeRecoveryImpact to learn what artifacts would be
 * deleted and whether any blockers exist (e.g. a CreditMemo already posted
 * to QuickBooks). If clean, the operator types a reason and confirms; the
 * dialog fetches the artifacts via Breeze, marks them deleted, saves, then
 * calls RecordRecovery to transition the RRD back to RECEIVING and emit the
 * ReturnRecovered JoNote.
 *
 * Patterns reused:
 *   - read-only impact endpoint mirrors ComputeProposedSettlement
 *   - server orchestration shape mirrors RecordReconciliation
 *   - client Breeze deletion uses entityAspect.setDeleted() + saveChanges()
 *     (see addon-station.component.ts, feature.component.ts, etc.)
 */
@Component({
  selector: "app-recover-rrd-dialog",
  templateUrl: "./recover-rrd-dialog.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class RecoverRrdDialogComponent extends DialogBaseComponent<RecoverRrdDialogParams> implements OnInit {
  rrd: ReturnRequestDetail | null = null;
  product: Product | null = null;
  impact: {
    itemDetailIds: string[];
    itemDetailAddonIds: number[];
    creditMemoIds: number[];
    blockers: string[];
    canRecover: boolean;
    currentPhase: string;
  } | null = null;

  reason = "";
  submitting = false;
  errorMessage: string | null = null;
  loadError: string | null = null;

  constructor(
    config: DynamicDialogConfig<RecoverRrdDialogParams>,
    dialogRef: DynamicDialogRef,
    domainService: DomainService,
    private http: HttpClient
  ) {
    super(config, dialogRef, domainService);
  }

  async ngOnInit() {
    const r = await this.uow.fetchEntityByKey(ReturnRequestDetail, this.params.rrdId);
    this.rrd = (r?.entity as ReturnRequestDetail) ?? null;
    if (this.rrd?.joDetail) {
      this.product = await this.dbQueryService.getProductById(this.rrd.joDetail.productId);
    }
    if (this.rrd) {
      try {
        const url = environment.apiRoot + "breeze/Uniformax/ComputeRecoveryImpact";
        const params = new HttpParams().set("rrdId", String(this.rrd.id));
        const raw = await firstValueFrom(this.http.get<RecoveryImpact>(url, { params }));
        // Server may return PascalCase or camelCase depending on serializer config.
        this.impact = {
          itemDetailIds:      raw.ItemDetailIds      ?? raw.itemDetailIds      ?? [],
          itemDetailAddonIds: raw.ItemDetailAddonIds ?? raw.itemDetailAddonIds ?? [],
          creditMemoIds:      raw.CreditMemoIds      ?? raw.creditMemoIds      ?? [],
          blockers:           raw.Blockers           ?? raw.blockers           ?? [],
          canRecover:         raw.CanRecover         ?? raw.canRecover         ?? false,
          currentPhase:       raw.CurrentPhase       ?? raw.currentPhase       ?? "",
        };
      } catch (err: any) {
        this.loadError = err?.error?.error || err?.message || "Failed to compute recovery impact.";
      }
    }
    this.isPageReady = true;
  }

  get variantText(): string {
    if (!this.product) return "";
    return (this.product as any).getFeatureChoicesExtract?.() ?? "";
  }

  get hasArtifacts(): boolean {
    return !!this.impact
      && (this.impact.itemDetailIds.length > 0
       || this.impact.creditMemoIds.length > 0
       || this.impact.itemDetailAddonIds.length > 0);
  }

  get canSubmit(): boolean {
    return !this.submitting
      && !!this.rrd
      && !!this.impact
      && this.impact.canRecover
      && this.reason.trim().length > 0;
  }

  async confirm() {
    if (!this.canSubmit || !this.rrd || !this.impact) return;
    this.submitting = true;
    this.errorMessage = null;
    try {
      const actor = this.authService.getUser()?.initials ?? "SYS";

      // Step 1: delete artifacts via Breeze if any exist.
      if (this.hasArtifacts) {
        if (this.impact.itemDetailIds.length > 0) {
          const itds = await this.dbQueryService.getItemDetailsForRecovery(this.impact.itemDetailIds);
          for (const itd of itds) {
            // Mark addons first so cascade order is explicit.
            const addons = (itd.itemDetailAddons ?? []).slice();
            for (const a of addons) {
              a.entityAspect.setDeleted();
            }
            itd.entityAspect.setDeleted();
          }
        }
        if (this.impact.creditMemoIds.length > 0) {
          const cms = await this.dbQueryService.getCreditMemosByIds(this.impact.creditMemoIds);
          for (const cm of cms) {
            cm.entityAspect.setDeleted();
          }
        }
        const saved = await this.dbSaveService.saveChanges();
        if (!saved) {
          // Validation dialog already shown.
          this.submitting = false;
          return;
        }
      }

      // Step 2: tell the server to transition the RRD and emit the JoNote.
      const url = environment.apiRoot + "breeze/Uniformax/RecordRecovery";
      await firstValueFrom(this.http.post(url, {
        rrdId: this.rrd.id,
        reason: this.reason.trim(),
        actor: actor,
      }));

      this.toastr.success(`RRD #${this.rrd.id} — recovered to RECEIVING`);
      this.dialogRef.close(true);
    } catch (err: any) {
      const msg = err?.error?.error || err?.message || "Failed to record recovery.";
      this.errorMessage = msg;
    } finally {
      this.submitting = false;
    }
  }

  cancel() {
    if (this.dbSaveService.hasChanges()) {
      this.dbSaveService.rejectChanges();
    }
    this.dialogRef.close(false);
  }
}
